<template>
 <div>
  <Navbar />
   <div class="quote-page-wrapper">
    <div class="quote-form-container">
      <header class="form-header">
        <h2>Get a fast, accurate manufacturing quote.</h2>
        <p>Upload your files and tell us about your project.</p>
      </header>

      <form name="manufacturing-quote" @submit.prevent="submitQuote" class="form-body" data-netlify="true" netlify-honeypot="bot-field" enctype="multipart/form-data">
        
        <input type="hidden" name="form-name" value="manufacturing-quote" />
        <p style="display: none;">
          <label>Don’t fill this out if you're human: <input name="bot-field" /></label>
        </p>

        <input type="hidden" name="manufacturingService" :value="form.manufacturingService" />
        <input type="hidden" name="country" :value="form.country" />
        
        <section class="form-section">
          <div class="section-title-row">
            <h3>Upload Your Design Files</h3>
            <span class="badge-required">Required</span>
          </div>
          
          <div class="upload-dropzone" :class="{ 'is-dragover': isDragging }"
               @dragover.prevent="isDragging = true" 
               @dragleave.prevent="isDragging = false"
               @drop.prevent="handleDrop"
               @click="triggerFileInput">
            <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
            </svg>
            <p class="upload-text">Drop files here or <span class="text-theme">Select files</span></p>
            <p class="upload-hint">Accepted file types: zip, rar, jpg, png, pdf, stl, stp, igs, step, x_t, sldprt, ipt, 3dxml, prt, sat, dae, fbx, obj, dwf, dxf, ifc, dgn, dwg. Max. file size: 64 MB, Max. files: 10.</p>
            
            <input 
              type="file" 
              ref="fileInputRef" 
              @change="handleFileSelect" 
              style="display: none" 
              multiple 
              name="designFiles"
            />
          </div>

          <div v-if="form.files.length > 0" class="file-list-preview">
            <p class="file-list-title">Selected Files ({{ form.files.length }}):</p>
            <ul class="file-list">
              <li v-for="(file, index) in form.files" :key="index" class="file-item">
                <span class="file-name">{{ file.name }}</span>
                <button type="button" class="remove-btn" @click="removeFile(index)" title="Remove file">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </li>
            </ul>
          </div>

          <div class="upload-note">
            Drop your files here or click to upload. Supported: 2D drawings & 3D CAD models. If your file format is not supported or you have multiple files, please compress them into a zip/rar file and upload again.
          </div>
        </section>

        <section class="form-section">
          <h3 class="section-title">Project Specifications</h3>
          
          <div class="form-group">
            <label>Manufacturing Service <span class="text-red">*</span></label>
            <div class="service-selector">
              <div 
                v-for="service in services" 
                :key="service.id"
                class="service-card"
                :class="{ 'active': form.manufacturingService === service.id }"
                @click="form.manufacturingService = service.id"
              >
                <span class="service-name">{{ service.name }}</span>
                <div v-if="form.manufacturingService === service.id" class="active-badge">
                  <svg viewBox="0 0 24 24" class="check-icon"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Quantity <span class="text-red">*</span></label>
            <input type="number" name="quantity" v-model="form.quantity" class="form-input" min="1">
            <span class="input-hint">Please enter a number greater than or equal to 1.</span>
          </div>

          <div class="form-group">
            <label>Color Requirement (Enter the PANTONE or RAL Color Code)</label>
            <input type="text" name="color" v-model="form.color" class="form-input" placeholder="e.g., Pantone 185 C, RAL 9003, Black matte...">
          </div>

          <div class="form-group">
            <label>Project Details / Special Requirements</label>
            <textarea name="details" v-model="form.details" class="form-input textarea" rows="4" placeholder="Any specific requirements for material, tolerance, surface finish, or other details..."></textarea>
          </div>
        </section>

        <section class="form-section">
          <h3 class="section-title">Your Contact Information</h3>
          
          <div class="form-row">
            <div class="form-group half-width">
              <label>Name <span class="text-red">*</span></label>
              <input type="text" name="name" v-model="form.name" class="form-input" required>
            </div>
            <div class="form-group half-width">
              <label>Email Address <span class="text-red">*</span></label>
              <input type="email" name="email" v-model="form.email" class="form-input" required>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group half-width">
              <label>Phone</label>
              <input type="tel" name="phone" v-model="form.phone" class="form-input">
            </div>

            <div class="form-group half-width" ref="countryDropdownRef">
              <label>Country or Location</label>
              <div class="custom-select" :class="{ 'is-open': isDropdownOpen }" @click="toggleDropdown">
                <span class="selected-value" :class="{ 'placeholder': !form.country }">
                  {{ form.country || 'Select your country...' }}
                </span>
                <svg class="chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>

                <div class="dropdown-panel" v-if="isDropdownOpen" @click.stop>
                  <div class="search-box">
                    <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input 
                      type="text" 
                      v-model="searchQuery" 
                      placeholder="Search country..." 
                      class="search-input"
                      ref="searchInputRef"
                    >
                  </div>
                  <ul class="options-list">
                    <li v-if="filteredCountries.length === 0" class="no-results">
                      No country found.
                    </li>
                    <li 
                      v-for="country in filteredCountries" 
                      :key="country"
                      class="option-item"
                      :class="{ 'is-selected': form.country === country }"
                      @click="selectCountry(country)"
                    >
                      {{ country }}
                      <svg v-if="form.country === country" class="check-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="submit-section">
          <div class="security-banner">
            <svg class="shield-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <p>All your uploads are secure and confidential. You can contact us (<a href="mailto:sundy.xcl@gmail.com">sundy.xcl@gmail.com</a>) to sign a NDA before sending any design files to us.</p>
          </div>

          <button type="submit" class="submit-button" :disabled="isLoading">
            {{ isLoading ? 'Submitting...' : 'Submit Quote Request' }}
          </button>

          <div class="trust-badges">
            <div class="badge-item">
              <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-width="2" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span>Files Kept Confidential</span>
            </div>
            <div class="badge-item">
              <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="2"/><path stroke-width="2" d="M12 6v6l4 2"/></svg>
              <span>NDA Available</span>
            </div>
            <div class="badge-item">
              <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-width="2" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              <span>Fast Quote in 24 Hours</span>
            </div>
            <div class="badge-item">
              <svg class="badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-width="2" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path stroke-width="2" d="M22 4L12 14.01l-3-3"/></svg>
              <span>ISO 9001 Certified</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
  <Footer />
 </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import Footer from './components/Footer.vue';
import Navbar from './components/Nav.vue';
// 1. ✨ 将 element-plus 替换为 element-ui 的 Message 组件
import { Message } from 'element-ui' 

const isDragging = ref(false)
const fileInputRef = ref(null)
const isLoading = ref(false); 

const form = reactive({
  manufacturingService: 'cnc', 
  quantity: 1,
  color: '',
  details: '',
  name: '',
  email: '',
  phone: '',
  country: 'United States',
  files: [] 
})

const services = [
  { id: 'cnc', name: 'CNC Machining' },
  { id: 'sheet_metal', name: 'Sheet Metal' },
  { id: '3d_printing', name: '3D Printing' },
  { id: 'Die_Casting', name: 'Die Casting' },
  { id: 'Compression_Molding_Services', name: 'Compression Molding Services' }
]

const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Congo-Brazzaville)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia", "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
]

const isDropdownOpen = ref(false)
const searchQuery = ref('')
const countryDropdownRef = ref(null)
const searchInputRef = ref(null)

const filteredCountries = computed(() => {
  if (!searchQuery.value) return countries
  return countries.filter(c => c.toLowerCase().includes(searchQuery.value.toLowerCase()))
})

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
  if (isDropdownOpen.value) {
    searchQuery.value = '' 
    nextTick(() => searchInputRef.value?.focus())
  }
}

const selectCountry = (country) => {
  form.country = country
  isDropdownOpen.value = false
}

const handleClickOutside = (event) => {
  if (countryDropdownRef.value && !countryDropdownRef.value.contains(event.target)) {
    isDropdownOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))

const triggerFileInput = () => {
  if (fileInputRef.value) fileInputRef.value.click()
}

const handleFileSelect = (e) => {
  const selectedFiles = Array.from(e.target.files)
  if (form.files.length + selectedFiles.length > 10) {
    Message.warning('You can only upload up to 10 files.');
    return;
  }
  form.files.push(...selectedFiles)
  e.target.value = '' 
}

const handleDrop = (e) => {
  isDragging.value = false
  const droppedFiles = Array.from(e.dataTransfer.files)
  if (form.files.length + droppedFiles.length > 10) {
    // ✨ 修改为 Message
    Message.warning('You can only upload up to 10 files.');
    return;
  }
  form.files.push(...droppedFiles)
}

const removeFile = (index) => {
  form.files.splice(index, 1)
}

const resetForm = () => {
  form.manufacturingService = 'cnc';
  form.quantity = 1;
  form.color = '';
  form.details = '';
  form.name = '';
  form.email = '';
  form.phone = '';
  form.country = 'United States';
  form.files = [];
};

const submitQuote = async () => {
  if (form.files.length === 0) {
  
    Message.warning('Please upload at least one design file.');
    return;
  }
  if (!form.name || !form.email) {

    Message.warning('Name and Email are required fields.');
    return;
  }

  isLoading.value = true; 

  const formData = new FormData()
  formData.append('form-name', 'manufacturing-quote') 
  
  formData.append('manufacturingService', form.manufacturingService)
  formData.append('quantity', form.quantity)
  formData.append('color', form.color)
  formData.append('details', form.details)
  formData.append('name', form.name)
  formData.append('email', form.email)
  formData.append('phone', form.phone)
  formData.append('country', form.country)
  
  form.files.forEach((file, index) => {
    formData.append(`designFile_${index + 1}`, file) 
  })

  // 本地拦截测试
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    console.log("Form Data:", Object.fromEntries(formData));
    console.log("Files:", form.files);
    
    
    Message.success('Local Test: Form submission simulated successfully!');
    
    resetForm(); 
    isLoading.value = false;
    return;
  }

  // 线上真实提交
  try {
    const response = await fetch('/', {
      method: 'POST',
      body: formData,
    })

    if (response.ok) {
      
      Message.success('Submitted successfully! We will contact you shortly.');
      resetForm(); 
    } else {
      throw new Error("Network response was not ok.")
    }
  } catch (error) {
    console.error('Submit failed:', error)
   
    Message.error('Submit failed, please check your network and try again.');
  } finally {
    isLoading.value = false; 
  }
}
</script>

<style scoped>
:root { --theme-color: #1eb897; --theme-hover: #0b7a5e; --theme-light: #e6f7f3; }
.quote-page-wrapper { background-color: #f3f4f6; padding: 40px 20px; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; margin-top: -60px; }
.quote-form-container { max-width: 800px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); padding: 40px; }
.form-header { margin-bottom: 32px; }
.form-header h2 { font-size: 24px; font-weight: 700; margin: 0 0 8px 0; color: #0f172a; }
.form-header p { margin: 0; color: #64748b; font-size: 15px; }
.form-section { margin-bottom: 40px; }
.section-title { font-size: 18px; font-weight: 600; margin: 0 0 20px 0; color: #0f172a; }
.section-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.section-title-row h3 { font-size: 18px; font-weight: 600; margin: 0; color: #0f172a; }
.badge-required { background-color: var(--theme-light, #e6f7f3); color: var(--theme-hover, #0b7a5e); font-size: 12px; padding: 2px 8px; border-radius: 12px; font-weight: 600; }
.text-red { color: #ef4444; }
.text-theme { color: var(--theme-color, #1eb897); font-weight: 600; }
.upload-dropzone { border: 2px dashed #cbd5e1; background-color: #f8fafc; border-radius: 8px; padding: 40px 20px; text-align: center; transition: all 0.3s ease; cursor: pointer; margin-bottom: 12px; }
.upload-dropzone:hover, .upload-dropzone.is-dragover { background-color: var(--theme-light, #e6f7f3); border-color: var(--theme-color, #1eb897); }
.upload-icon { width: 32px; height: 32px; color: #64748b; margin-bottom: 12px; transition: color 0.3s; }
.upload-dropzone:hover .upload-icon { color: var(--theme-color, #1eb897); }
.upload-text { font-size: 16px; margin: 0 0 8px 0; color: #334155; }
.upload-hint { font-size: 12px; color: #94a3b8; margin: 0; line-height: 1.5; }
.upload-note { background-color: #f8fafc; padding: 12px 16px; border-radius: 6px; font-size: 13px; color: #64748b; line-height: 1.5; }
.form-group { margin-bottom: 20px; display: flex; flex-direction: column; position: relative; }
.form-group label { font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #1e293b; }
.form-input { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; font-size: 14px; color: #0f172a; transition: border-color 0.2s, box-shadow 0.2s; background-color: #ffffff; width: 100%; box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
.form-input:focus { outline: none; border-color: var(--theme-color, #1eb897); box-shadow: 0 0 0 3px rgba(30, 184, 151, 0.15); }
.form-input::placeholder { color: #94a3b8; }
.textarea { resize: vertical; min-height: 80px; }
.input-hint { font-size: 12px; color: #64748b; margin-top: 6px; }
.form-row { display: flex; gap: 20px; }
.half-width { flex: 1; }
.service-selector { display: flex; gap: 12px; flex-wrap: wrap; }
.service-card { position: relative; padding: 10px 16px; border: 1px solid #cbd5e1; border-radius: 6px; background: #ffffff; cursor: pointer; transition: all 0.2s; overflow: hidden; user-select: none; font-size: 14px; color: #475569; }
.service-card:hover { background: #f8fafc; border-color: #94a3b8; }
.service-card.active { background: var(--theme-light, #e6f7f3); border-color: var(--theme-color, #1eb897); color: var(--theme-hover, #0b7a5e); font-weight: 600; }
.active-badge { position: absolute; bottom: 0; right: 0; width: 16px; height: 16px; background-color: var(--theme-color, #1eb897); clip-path: polygon(100% 0, 0 100%, 100% 100%); border-bottom-right-radius: 4px; }
.check-icon { position: absolute; bottom: 1px; right: 1px; width: 9px; height: 9px; fill: #ffffff; }
.custom-select { position: relative; border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; font-size: 14px; background-color: #ffffff; cursor: pointer; display: flex; justify-content: space-between; align-items: center; user-select: none; transition: border-color 0.2s, box-shadow 0.2s; }
.custom-select.is-open { border-color: var(--theme-color, #1eb897); box-shadow: 0 0 0 3px rgba(30, 184, 151, 0.15); }
.selected-value { color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.selected-value.placeholder { color: #94a3b8; }
.chevron-icon { width: 16px; height: 16px; color: #64748b; transition: transform 0.2s; }
.custom-select.is-open .chevron-icon { transform: rotate(180deg); }
.dropdown-panel { position: absolute; top: calc(100% + 4px); left: 0; width: 100%; background: #ffffff; border: 1px solid #cbd5e1; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); z-index: 50; overflow: hidden; }
.search-box { padding: 8px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; background: #f8fafc; }
.search-icon { width: 14px; height: 14px; color: #94a3b8; margin-right: 8px; flex-shrink: 0; }
.search-input { border: none; background: transparent; outline: none; width: 100%; font-size: 13px; color: #334155; }
.search-input::placeholder { color: #94a3b8; }
.options-list { max-height: 200px; overflow-y: auto; margin: 0; padding: 4px 0; list-style: none; }
.option-item { padding: 8px 12px; font-size: 14px; color: #334155; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: background-color 0.15s; }
.option-item:hover { background-color: #f1f5f9; }
.option-item.is-selected { background-color: var(--theme-light, #e6f7f3); color: var(--theme-hover, #0b7a5e); font-weight: 500; }
.no-results { padding: 12px; text-align: center; font-size: 13px; color: #94a3b8; }
.check-icon-small { width: 16px; height: 16px; color: var(--theme-color, #1eb897); }
.options-list::-webkit-scrollbar { width: 6px; }
.options-list::-webkit-scrollbar-track { background: transparent; }
.options-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.options-list::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
.security-banner { display: flex; align-items: flex-start; gap: 12px; background-color: #fefce8; border: 1px solid #fef08a; padding: 16px; border-radius: 8px; margin-bottom: 24px; }
.shield-icon { width: 20px; height: 20px; color: #ca8a04; flex-shrink: 0; margin-top: 2px; }
.security-banner p { margin: 0; font-size: 13px; color: #854d0e; line-height: 1.5; }
.security-banner a { color: #ca8a04; font-weight: 600; text-decoration: none; }
.security-banner a:hover { text-decoration: underline; }

.submit-button {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: var(--theme-color, #1eb897);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 16px 24px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease; 
  margin-bottom: 24px;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--theme-hover, #0b7a5e);
}

/* ✨ 给禁用状态 (Loading) 添加视觉反馈 */
.submit-button:disabled {
  background-color: #94a3b8; 
  cursor: not-allowed;       
  opacity: 0.8;
  animation: pulse 1.5s infinite; 
}

/* ✨ 呼吸动画关键帧 */
@keyframes pulse {
  0% { opacity: 0.8; }
  50% { opacity: 0.5; }
  100% { opacity: 0.8; }
}

.trust-badges { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 16px; }
.badge-item { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; color: #475569; background-color: #f8fafc; padding: 8px 14px; border-radius: 20px; border: 1px solid #e2e8f0; }
.badge-icon { width: 14px; height: 14px; color: var(--theme-color, #1eb897); }
@media (max-width: 640px) { .form-row { flex-direction: column; gap: 0; } .quote-form-container { padding: 24px; } }

/* ✨ 文件预览区域样式 */
.file-list-preview { margin-bottom: 12px; padding: 12px 16px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; }
.file-list-title { margin: 0 0 10px 0; font-size: 13px; font-weight: 600; color: #1e293b; }
.file-list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
.file-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 4px; transition: border-color 0.2s; }
.file-item:hover { border-color: #cbd5e1; }
.file-name { font-size: 13px; color: #475569; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 12px; }
.remove-btn { background: transparent; border: none; padding: 4px; border-radius: 4px; cursor: pointer; color: #94a3b8; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; flex-shrink: 0; }
.remove-btn:hover { background-color: #fee2e2; color: #ef4444; }
.remove-btn svg { width: 16px; height: 16px; }
</style>